import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  TrophyIcon,
  FireIcon,
  BoltIcon,
  ArrowTrendingUpIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import PlayerStatsRow from '../components/boxscore/PlayerStatsRow';
import api from '../services/api';
import { calculateOverallHeat } from '../utils/playerTrends';

const BoxScore = () => {
  const { sport, gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [boxScore, setBoxScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');

  // Default sort column based on sport
  const getDefaultSortColumn = () => {
    if (sport === 'nba') return 'points';
    if (sport === 'nhl') return 'points';  // NHL points = goals + assists
    if (sport === 'nfl') return 'passing_yards';  // Total yards
    if (sport === 'mlb') return 'hits';
    return null;
  };

  const [sortColumn, setSortColumn] = useState(getDefaultSortColumn());
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch game details and box score from stats endpoint
        const statsResponse = await api.get(`/${sport}/stats/games/${gameId}`);
        const statsData = statsResponse.data;

        // Debug: Log the response to see what we're getting
        console.log('Stats API Response:', statsData);

        // Extract game info from stats response
        if (statsData.game) {
          setGame(statsData.game);
          setBoxScore(statsData);
          console.log('Box Score Data:', statsData);
          console.log('Player Stats:', statsData.player_stats);
        } else {
          throw new Error('Invalid response format from stats endpoint');
        }
      } catch (err) {
        console.error('Error fetching game details:', err);
        setError('Failed to load game details');
      } finally {
        setLoading(false);
      }
    };

    fetchGameDetails();
  }, [sport, gameId]);

  const handleBack = () => {
    navigate(`/leagues/${sport}`);
  };

  // Calculate momentum data for visualization
  const momentumData = useMemo(() => {
    if (!boxScore || !game) return [];

    // Simulated quarter-by-quarter momentum (in production, get from API)
    // TODO: Replace with actual quarter/period scores from API
    const quarters = sport === 'nhl' ? 3 : 4;
    const homeScore = game.home_score || 0;
    const awayScore = game.away_score || 0;

    return Array.from({ length: quarters }, (_, i) => ({
      period: sport === 'nhl' ? `P${i + 1}` : `Q${i + 1}`,
      home: Math.floor((homeScore / quarters) * (i + 1)) + Math.floor(Math.random() * 5),
      away: Math.floor((awayScore / quarters) * (i + 1)) + Math.floor(Math.random() * 5),
    }));
  }, [boxScore, game, sport]);

  // Calculate performance radar data
  const performanceRadarData = useMemo(() => {
    if (!boxScore || !boxScore.home_team_stats || !boxScore.away_team_stats) return [];

    const data = [];
    const homeStats = boxScore.home_team_stats;
    const awayStats = boxScore.away_team_stats;

    // Common categories
    if (sport === 'nfl' || sport === 'ncaa_f') {
      if (homeStats.passing_yards !== undefined) {
        data.push({
          category: 'Offense',
          home: Math.min(100, ((homeStats.passing_yards || 0) + (homeStats.rushing_yards || 0)) / 5),
          away: Math.min(100, ((awayStats.passing_yards || 0) + (awayStats.rushing_yards || 0)) / 5),
        });
      }
    }

    data.push({
      category: 'Defense',
      home: Math.max(0, 100 - ((awayStats.turnovers || 0) * 15)),
      away: Math.max(0, 100 - ((homeStats.turnovers || 0) * 15)),
    });

    data.push({
      category: 'Discipline',
      home: Math.max(0, 100 - ((homeStats.fouls || homeStats.penalties || 0) * 4)),
      away: Math.max(0, 100 - ((awayStats.fouls || awayStats.penalties || 0) * 4)),
    });

    // Sport-specific categories
    if (sport === 'nhl' && homeStats.shots) {
      data.push({
        category: 'Shots',
        home: Math.min(100, (homeStats.shots || 0) * 2),
        away: Math.min(100, (awayStats.shots || 0) * 2),
      });
    }

    if (sport === 'nba') {
      if (homeStats.total_rebounds) {
        data.push({
          category: 'Rebounds',
          home: Math.min(100, (homeStats.total_rebounds || 0) * 1.5),
          away: Math.min(100, (awayStats.total_rebounds || 0) * 1.5),
        });
      }
      if (homeStats.assists) {
        data.push({
          category: 'Assists',
          home: Math.min(100, (homeStats.assists || 0) * 3),
          away: Math.min(100, (awayStats.assists || 0) * 3),
        });
      }
      if (homeStats.fg_pct) {
        data.push({
          category: 'Shooting',
          home: homeStats.fg_pct || 0,
          away: awayStats.fg_pct || 0,
        });
      }
    }

    if (sport === 'mlb' && homeStats.hits) {
      data.push({
        category: 'Hits',
        home: Math.min(100, (homeStats.hits || 0) * 8),
        away: Math.min(100, (awayStats.hits || 0) * 8),
      });
    }

    // Efficiency (calculated from score differential)
    const homeScore = game.home_score || 0;
    const awayScore = game.away_score || 0;
    const totalScore = homeScore + awayScore;
    if (totalScore > 0) {
      data.push({
        category: 'Efficiency',
        home: (homeScore / totalScore) * 100,
        away: (awayScore / totalScore) * 100,
      });
    }

    return data;
  }, [boxScore, game, sport]);

  // Calculate key stats comparison for bar chart
  const statsComparisonData = useMemo(() => {
    if (!boxScore || !boxScore.home_team_stats || !boxScore.away_team_stats) return [];

    const data = [];
    const homeStats = boxScore.home_team_stats;
    const awayStats = boxScore.away_team_stats;

    // Sport-specific stats
    if (sport === 'nfl' || sport === 'ncaa_f') {
      data.push(
        {
          stat: 'Pass Yds',
          home: homeStats.passing_yards || 0,
          away: awayStats.passing_yards || 0,
        },
        {
          stat: 'Rush Yds',
          home: homeStats.rushing_yards || 0,
          away: awayStats.rushing_yards || 0,
        },
        {
          stat: 'Total Yds',
          home: (homeStats.passing_yards || 0) + (homeStats.rushing_yards || 0),
          away: (awayStats.passing_yards || 0) + (awayStats.rushing_yards || 0),
        }
      );
    } else if (sport === 'nhl') {
      data.push(
        {
          stat: 'Shots',
          home: homeStats.shots || 0,
          away: awayStats.shots || 0,
        },
        {
          stat: 'PIM',
          home: homeStats.penalty_minutes || 0,
          away: awayStats.penalty_minutes || 0,
        }
      );
    } else if (sport === 'nba') {
      data.push(
        {
          stat: 'Rebounds',
          home: homeStats.total_rebounds || 0,
          away: awayStats.total_rebounds || 0,
        },
        {
          stat: 'Assists',
          home: homeStats.assists || 0,
          away: awayStats.assists || 0,
        },
        {
          stat: 'FG%',
          home: homeStats.fg_pct || 0,
          away: awayStats.fg_pct || 0,
        }
      );
    } else if (sport === 'mlb') {
      data.push(
        {
          stat: 'Hits',
          home: homeStats.hits || 0,
          away: awayStats.hits || 0,
        },
        {
          stat: 'Errors',
          home: homeStats.errors || 0,
          away: awayStats.errors || 0,
        },
        {
          stat: 'Strikeouts',
          home: homeStats.strikeouts || 0,
          away: awayStats.strikeouts || 0,
        }
      );
    }

    return data;
  }, [boxScore, sport]);

  // Sorting handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Group NFL players by position
  const nflPlayersByPosition = useMemo(() => {
    if (sport !== 'nfl' || !boxScore || !boxScore.player_stats) return null;

    const qbs = [];
    const rbs = [];
    const receivers = []; // WR and TE

    boxScore.player_stats.forEach(player => {
      if (player.position === 'QB') {
        qbs.push(player);
      } else if (player.position === 'RB') {
        rbs.push(player);
      } else if (player.position === 'WR' || player.position === 'TE') {
        receivers.push(player);
      }
    });

    // Sort each group by hot/cold performance (hot players first)
    const sortByHeat = (a, b) => {
      const aHeat = calculateOverallHeat(a, sport);
      const bHeat = calculateOverallHeat(b, sport);
      const heatOrder = { 'hot': 3, 'neutral': 2, 'cold': 1 };
      const heatDiff = (heatOrder[bHeat] || 2) - (heatOrder[aHeat] || 2);

      // If same heat level, sort by total yards as tiebreaker
      if (heatDiff === 0) {
        const aYards = (a.passing_yards || 0) + (a.rushing_yards || 0) + (a.receiving_yards || 0);
        const bYards = (b.passing_yards || 0) + (b.rushing_yards || 0) + (b.receiving_yards || 0);
        return bYards - aYards;
      }
      return heatDiff;
    };

    qbs.sort(sortByHeat);
    rbs.sort(sortByHeat);
    receivers.sort(sortByHeat);

    return { qbs, rbs, receivers };
  }, [sport, boxScore]);

  // Sorted player stats (for non-NFL sports)
  const sortedPlayerStats = useMemo(() => {
    if (!boxScore || !boxScore.player_stats) return [];

    let stats = [...boxScore.player_stats];

    // Filter out defensive players for NFL - only show offensive players (QB, RB, WR, TE)
    if (sport === 'nfl') {
      stats = stats.filter(player => player.position !== 'DEF');
    }

    // Default sort by hot/cold performance if no sort column specified
    if (!sortColumn) {
      return stats.sort((a, b) => {
        const aHeat = calculateOverallHeat(a, sport);
        const bHeat = calculateOverallHeat(b, sport);
        const heatOrder = { 'hot': 3, 'neutral': 2, 'cold': 1 };
        return (heatOrder[bHeat] || 2) - (heatOrder[aHeat] || 2);
      });
    }

    return stats.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Special handling for NFL total yards and touchdowns
      if (sport === 'nfl') {
        if (sortColumn === 'passing_yards') {
          aVal = (a.passing_yards || 0) + (a.rushing_yards || 0) + (a.receiving_yards || 0);
          bVal = (b.passing_yards || 0) + (b.rushing_yards || 0) + (b.receiving_yards || 0);
        } else if (sortColumn === 'passing_touchdowns') {
          aVal = (a.passing_touchdowns || 0) + (a.rushing_touchdowns || 0) + (a.receiving_touchdowns || 0);
          bVal = (b.passing_touchdowns || 0) + (b.rushing_touchdowns || 0) + (b.receiving_touchdowns || 0);
        }
      }

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = -Infinity;
      if (bVal === null || bVal === undefined) bVal = -Infinity;

      // For player_name, use string comparison
      if (sortColumn === 'player_name') {
        aVal = (aVal || `Player #${a.player_id}`).toLowerCase();
        bVal = (bVal || `Player #${b.player_id}`).toLowerCase();
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });
  }, [boxScore, sortColumn, sortDirection, sport]);

  // Team color mapping - organized by sport to avoid duplicate keys
  const teamColorsBySport = {
    nba: {
      'ATL': '#E03A3E', 'BOS': '#007A33', 'BKN': '#000000', 'CHA': '#1D1160',
      'CHI': '#CE1141', 'CLE': '#860038', 'DAL': '#00538C', 'DEN': '#0E2240',
      'DET': '#C8102E', 'GSW': '#1D428A', 'HOU': '#CE1141', 'IND': '#002D62',
      'LAC': '#C8102E', 'LAL': '#552583', 'MEM': '#5D76A9', 'MIA': '#98002E',
      'MIL': '#00471B', 'MIN': '#0C2340', 'NOP': '#0C2340', 'NYK': '#006BB6',
      'OKC': '#007AC1', 'ORL': '#0077C0', 'PHI': '#006BB6', 'PHX': '#1D1160',
      'POR': '#E03A3E', 'SAC': '#5A2D81', 'SAS': '#C4CED4', 'TOR': '#CE1141',
      'UTA': '#002B5C', 'WAS': '#002B5C',
    },
    nhl: {
      'ANA': '#F47A38', 'ARI': '#8C2633', 'BOS': '#FFB81C', 'BUF': '#002654',
      'CGY': '#C8102E', 'CAR': '#CC0000', 'CHI': '#CF0A2C', 'COL': '#6F263D',
      'CBJ': '#002654', 'DAL': '#006847', 'DET': '#CE1126', 'EDM': '#FF4C00',
      'FLA': '#041E42', 'LAK': '#111111', 'MIN': '#154734', 'MTL': '#AF1E2D',
      'NSH': '#FFB81C', 'NJD': '#CE1126', 'NYI': '#00539B', 'NYR': '#0038A8',
      'OTT': '#C52032', 'PHI': '#F74902', 'PIT': '#FCB514', 'SJS': '#006D75',
      'SEA': '#001628', 'STL': '#002F87', 'TBL': '#002868', 'TOR': '#00205B',
      'VAN': '#00205B', 'VGK': '#B4975A', 'WSH': '#C8102E', 'WPG': '#041E42',
    },
    nfl: {
      'ARI': '#97233F', 'ATL': '#A71930', 'BAL': '#241773', 'BUF': '#00338D',
      'CAR': '#0085CA', 'CHI': '#C83803', 'CIN': '#FB4F14', 'CLE': '#311D00',
      'DAL': '#003594', 'DEN': '#FB4F14', 'DET': '#0076B6', 'GB': '#203731',
      'HOU': '#03202F', 'IND': '#002C5F', 'JAX': '#006778', 'KC': '#E31837',
      'LAC': '#0080C6', 'LAR': '#003594', 'LV': '#A5ACAF', 'MIA': '#008E97',  // Changed LV from black to silver
      'MIN': '#4F2683', 'NE': '#002244', 'NO': '#D3BC8D', 'NYG': '#0B2265',
      'NYJ': '#125740', 'PHI': '#004C54', 'PIT': '#FFB612', 'SF': '#AA0000',
      'SEA': '#69BE28', 'TB': '#D50A0A', 'TEN': '#0C2340', 'WAS': '#5A1414',  // Changed SEA to action green
    },
    mlb: {
      'LAA': '#BA0021', 'HOU': '#002D62', 'OAK': '#003831', 'TOR': '#134A8E',
      'ATL': '#CE1141', 'MIL': '#FFC52F', 'STL': '#C41E3A', 'CHC': '#0E3386',
      'AZ': '#A71930', 'LAD': '#005A9C', 'SF': '#FD5A1E', 'CLE': '#E31937',
      'SEA': '#0C2C56', 'MIA': '#00A3E0', 'NYM': '#002D72', 'WAS': '#AB0003',
      'BAL': '#DF4601', 'SD': '#2F241D', 'PHI': '#E81828', 'PIT': '#FDB827',
      'TEX': '#003278', 'TB': '#092C5C', 'BOS': '#BD3039', 'CIN': '#C6011F',
      'COL': '#33006F', 'KC': '#004687', 'DET': '#0C2C56', 'MIN': '#002B5C',
      'CWS': '#27251F', 'NYY': '#003087',
    }
  };

  // Get team color for a player
  const getTeamColor = (teamId) => {
    if (!boxScore) return '#9CA3AF'; // gray-400 default

    // Find team abbreviation
    let teamAbbr = null;
    if (game.home_team?.id === teamId) {
      teamAbbr = game.home_team?.abbreviation;
    } else if (game.away_team?.id === teamId) {
      teamAbbr = game.away_team?.abbreviation;
    }

    // Get colors for current sport
    const sportColors = teamColorsBySport[sport] || {};
    return sportColors[teamAbbr] || '#9CA3AF';
  };

  // Helper function to calculate trend for NFL stats
  const calculateNFLTrend = (gameStat, seasonTotal, gamesPlayed) => {
    if (!gamesPlayed || gamesPlayed === 0) return 'neutral';
    const seasonAvg = seasonTotal / gamesPlayed;
    const difference = gameStat - seasonAvg;
    const percentDiff = Math.abs(difference) / (seasonAvg || 1);

    if (percentDiff > 0.25) {
      return difference > 0 ? 'up' : 'down';
    }
    return 'neutral';
  };

  // Helper to get trend arrow
  const getTrendArrow = (trend) => {
    if (trend === 'up') return '↑';
    if (trend === 'down') return '↓';
    return '';
  };

  // Helper to get trend color class
  const getTrendColorClass = (trend) => {
    if (trend === 'up') return 'text-green-400';
    if (trend === 'down') return 'text-red-400';
    return 'text-gray-400';
  };

  // Helper to calculate overall NFL performance and get emoji
  const getNFLPerformanceEmoji = (player) => {
    if (!player.season_games_played || player.season_games_played === 0) return null;

    let hotCount = 0;
    let coldCount = 0;

    // For QBs
    if (player.passing_yards > 0 || player.passing_attempts > 0) {
      const passYardsTrend = calculateNFLTrend(player.passing_yards || 0, player.season_passing_yards || 0, player.season_games_played);
      const passTDTrend = calculateNFLTrend(player.passing_touchdowns || 0, player.season_passing_tds || 0, player.season_games_played);
      const intTrend = calculateNFLTrend(player.interceptions || 0, player.season_interceptions || 0, player.season_games_played);

      if (passYardsTrend === 'up') hotCount++;
      if (passYardsTrend === 'down') coldCount++;
      if (passTDTrend === 'up') hotCount++;
      if (passTDTrend === 'down') coldCount++;
      if (intTrend === 'down') hotCount++; // Fewer INTs is good
      if (intTrend === 'up') coldCount++;  // More INTs is bad
    }

    // For RBs and receivers
    if (player.rushing_yards > 0 || player.rushing_attempts > 0) {
      const rushYardsTrend = calculateNFLTrend(player.rushing_yards || 0, player.season_rushing_yards || 0, player.season_games_played);
      const rushTDTrend = calculateNFLTrend(player.rushing_touchdowns || 0, player.season_rushing_tds || 0, player.season_games_played);

      if (rushYardsTrend === 'up') hotCount++;
      if (rushYardsTrend === 'down') coldCount++;
      if (rushTDTrend === 'up') hotCount++;
      if (rushTDTrend === 'down') coldCount++;
    }

    if (player.receiving_yards > 0 || player.receptions > 0) {
      const recYardsTrend = calculateNFLTrend(player.receiving_yards || 0, player.season_receiving_yards || 0, player.season_games_played);
      const recTDTrend = calculateNFLTrend(player.receiving_touchdowns || 0, player.season_receiving_tds || 0, player.season_games_played);

      if (recYardsTrend === 'up') hotCount++;
      if (recYardsTrend === 'down') coldCount++;
      if (recTDTrend === 'up') hotCount++;
      if (recTDTrend === 'down') coldCount++;
    }

    // Determine emoji based on hot vs cold count
    if (hotCount >= 2 && hotCount > coldCount) return '🔥';
    if (coldCount >= 2 && coldCount > hotCount) return '❄️';
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center h-96"
          >
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-gray-300">Loading game details...</p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-6">
        <div className="max-w-7xl mx-auto">
          <Button variant="secondary" size="sm" onClick={handleBack} className="mb-4">
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back
          </Button>
          <Card className="p-8 text-center bg-gray-800 border-gray-700">
            <p className="text-red-400">{error || 'Game not found'}</p>
          </Card>
        </div>
      </div>
    );
  }

  const isLive = game.status === 'in_progress';
  const isFinal = game.status === 'final' || game.status === 'completed' || game.status === 'Final';
  const homeScore = game.home_score || 0;
  const awayScore = game.away_score || 0;
  const winner = homeScore > awayScore ? 'home' : awayScore > homeScore ? 'away' : 'tie';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header - Compact & Dense */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-6"
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBack}
            className="bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Back to Schedule
          </Button>

          <div className="flex items-center gap-3">
            {isLive && (
              <Badge variant="danger" size="sm" className="animate-pulse">
                <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1"></span>
                LIVE
              </Badge>
            )}
            {isFinal && (
              <Badge variant="primary" size="sm" className="bg-blue-600 text-white">
                FINAL
              </Badge>
            )}
            <span className="text-sm text-gray-400">
              {(() => {
                // Add T12:00:00 to avoid timezone issues with date-only strings
                const dateStr = game.date || game.game_date;
                const dateObj = dateStr?.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T12:00:00');
                return dateObj.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                });
              })()}
            </span>
          </div>
        </motion.div>

        {/* Compact Score Header - The WOW Factor Starts Here */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 rounded-xl shadow-2xl border border-gray-700 p-6 mb-6 overflow-hidden relative"
        >
          {/* Background accent */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>

          <div className="relative flex flex-col md:grid md:grid-cols-5 gap-4 items-center">
            {/* Away Team */}
            <div
              className={`md:col-span-2 w-full md:text-right transition-all duration-300 ${
                winner === 'away' ? 'opacity-100 md:scale-105' : 'opacity-70'
              }`}
            >
              <div className="flex items-center justify-between md:justify-end gap-2 md:gap-4">
                {game.away_team?.logo && (
                  <motion.img
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    src={game.away_team.logo}
                    alt={game.away_team.name}
                    className="w-12 h-12 md:w-16 md:h-16 object-contain"
                  />
                )}
                <div className="flex-1 md:flex-none md:pr-2">
                  <h2 className="text-base md:text-xl font-bold text-white mb-1">
                    {game.away_team?.name || game.away_team || 'Away'}
                  </h2>
                  <p className="text-xs md:text-sm text-gray-400">
                    {game.away_team?.abbreviation || game.away_team_abbr}
                  </p>
                </div>
                {/* Mobile Score - Away */}
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                  className={`md:hidden text-3xl font-bold font-mono transition-all duration-300 ${
                    winner === 'away'
                      ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]'
                      : 'text-gray-500'
                  }`}
                >
                  {awayScore}
                </motion.span>
              </div>
            </div>

            {/* Score - The Centerpiece (Desktop only) */}
            <div className="hidden md:block text-center">
              <div className="flex items-center justify-center gap-3">
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                  className={`text-4xl font-bold font-mono transition-all duration-300 ${
                    winner === 'away'
                      ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]'
                      : 'text-gray-500'
                  }`}
                >
                  {awayScore}
                </motion.span>
                <span className="text-2xl text-gray-600">-</span>
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                  className={`text-4xl font-bold font-mono transition-all duration-300 ${
                    winner === 'home'
                      ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]'
                      : 'text-gray-500'
                  }`}
                >
                  {homeScore}
                </motion.span>
              </div>
            </div>

            {/* Home Team */}
            <div
              className={`md:col-span-2 w-full md:text-left transition-all duration-300 ${
                winner === 'home' ? 'opacity-100 md:scale-105' : 'opacity-70'
              }`}
            >
              <div className="flex items-center justify-between md:justify-start gap-2 md:gap-4">
                {/* Mobile Score - Home */}
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                  className={`md:hidden text-3xl font-bold font-mono transition-all duration-300 ${
                    winner === 'home'
                      ? 'text-white drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]'
                      : 'text-gray-500'
                  }`}
                >
                  {homeScore}
                </motion.span>
                <div className="flex-1 md:flex-none md:pl-2">
                  <h2 className="text-base md:text-xl font-bold text-white mb-1 text-right md:text-left">
                    {game.home_team?.name || game.home_team || 'Home'}
                  </h2>
                  <p className="text-xs md:text-sm text-gray-400 text-right md:text-left">
                    {game.home_team?.abbreviation || game.home_team_abbr}
                  </p>
                </div>
                {game.home_team?.logo && (
                  <motion.img
                    initial={{ rotate: 180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    src={game.home_team.logo}
                    alt={game.home_team.name}
                    className="w-12 h-12 md:w-16 md:h-16 object-contain"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats Bar */}
          {boxScore && isFinal && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="relative mt-4 pt-4 border-t border-gray-700 grid grid-cols-3 gap-4 text-center"
            >
              {sport === 'nfl' || sport === 'ncaa_f' ? (
                <>
                  <QuickStat
                    label="Total Yards"
                    value={`${(boxScore.away_team_stats?.passing_yards || 0) + (boxScore.away_team_stats?.rushing_yards || 0)} - ${
                      (boxScore.home_team_stats?.passing_yards || 0) + (boxScore.home_team_stats?.rushing_yards || 0)
                    }`}
                  />
                  <QuickStat
                    label="Turnovers"
                    value={`${boxScore.away_team_stats?.turnovers || 0} - ${boxScore.home_team_stats?.turnovers || 0}`}
                  />
                </>
              ) : sport === 'nhl' ? (
                <>
                  <QuickStat
                    label="Shots on Goal"
                    value={`${boxScore.away_team_stats?.shots || 0} - ${boxScore.home_team_stats?.shots || 0}`}
                  />
                  <QuickStat
                    label="Power Play"
                    value={`${boxScore.away_team_stats?.power_play_goals || 0}/${boxScore.away_team_stats?.power_play_opportunities || 0} - ${boxScore.home_team_stats?.power_play_goals || 0}/${boxScore.home_team_stats?.power_play_opportunities || 0}`}
                  />
                </>
              ) : sport === 'nba' ? (
                <>
                  <QuickStat
                    label="Rebounds"
                    value={`${boxScore.away_team_stats?.total_rebounds || 0} - ${boxScore.home_team_stats?.total_rebounds || 0}`}
                  />
                  <QuickStat
                    label="Assists"
                    value={`${boxScore.away_team_stats?.assists || 0} - ${boxScore.home_team_stats?.assists || 0}`}
                  />
                </>
              ) : sport === 'mlb' ? (
                <>
                  <QuickStat label="Hits" value={`${boxScore.away_team_stats?.hits || 0} - ${boxScore.home_team_stats?.hits || 0}`} />
                  <QuickStat
                    label="Errors"
                    value={`${boxScore.away_team_stats?.errors || 0} - ${boxScore.home_team_stats?.errors || 0}`}
                  />
                </>
              ) : null}
              <QuickStat label="Venue" value={game.venue || 'TBD'} />
            </motion.div>
          )}
        </motion.div>

        {/* Tab Navigation */}
        {boxScore && isFinal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex gap-2 mb-6 overflow-x-auto pb-2"
          >
            {[
              { id: 'overview', label: 'Overview', icon: ChartBarIcon },
              { id: 'players', label: 'Player Stats', icon: UsersIcon },
            ].map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                  selectedTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Content Area - Overview Tab */}
        {boxScore && isFinal && selectedTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Stats Comparison Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <ChartBarIcon className="w-6 h-6 text-blue-500" />
                  Statistical Comparison
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statsComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="stat" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem',
                      }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                    <Legend />
                    <Bar
                      dataKey="away"
                      fill="#EF4444"
                      name={game.away_team?.abbreviation || 'Away'}
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="home"
                      fill="#3B82F6"
                      name={game.home_team?.abbreviation || 'Home'}
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </motion.div>

            {/* Performance Radar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <BoltIcon className="w-6 h-6 text-yellow-500" />
                  Performance Analysis
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={performanceRadarData}>
                    <PolarGrid stroke="#374151" />
                    <PolarAngleAxis dataKey="category" stroke="#9CA3AF" />
                    <PolarRadiusAxis stroke="#9CA3AF" angle={90} domain={[0, 100]} />
                    <Radar
                      name={game.away_team?.abbreviation || 'Away'}
                      dataKey="away"
                      stroke="#EF4444"
                      fill="#EF4444"
                      fillOpacity={0.5}
                    />
                    <Radar
                      name={game.home_team?.abbreviation || 'Home'}
                      dataKey="home"
                      stroke="#3B82F6"
                      fill="#3B82F6"
                      fillOpacity={0.5}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </Card>
            </motion.div>

            {/* Detailed Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="lg:col-span-2"
            >
              <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4">Detailed Statistics</h3>
                <DetailedStats boxScore={boxScore} sport={sport} />
              </Card>
            </motion.div>
          </motion.div>
        )}

        {/* Analytics Tab */}
        {boxScore && isFinal && selectedTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Momentum Tracker */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
                  Game Momentum Tracker
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={momentumData}>
                    <defs>
                      <linearGradient id="colorAway" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorHome" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="period" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '0.5rem',
                      }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="away"
                      stroke="#EF4444"
                      fillOpacity={1}
                      fill="url(#colorAway)"
                      name={game.away_team?.abbreviation || 'Away'}
                    />
                    <Area
                      type="monotone"
                      dataKey="home"
                      stroke="#3B82F6"
                      fillOpacity={1}
                      fill="url(#colorHome)"
                      name={game.home_team?.abbreviation || 'Home'}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                <p className="text-sm text-gray-400 mt-4 text-center">
                  <FireIcon className="w-4 h-4 inline text-orange-500 mr-1" />
                  Momentum shifts throughout the game
                </p>
              </Card>
            </motion.div>

            {/* Key Performance Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
                <h3 className="text-xl font-bold text-white mb-4">Key Performance Indicators</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <KPICard
                    title="Winner Determination"
                    value={
                      winner === 'home'
                        ? game.home_team?.abbreviation || 'Home'
                        : winner === 'away'
                        ? game.away_team?.abbreviation || 'Away'
                        : 'Tie'
                    }
                    subtitle={`by ${Math.abs(homeScore - awayScore)} ${sport === 'mlb' ? 'runs' : sport === 'nhl' ? 'goals' : 'points'}`}
                    icon={TrophyIcon}
                    iconColor="text-yellow-500"
                  />
                  <KPICard
                    title="Competitive Balance"
                    value={Math.abs(homeScore - awayScore) <= 7 ? 'Close Game' : 'Blowout'}
                    subtitle={`${Math.abs(homeScore - awayScore)} ${sport === 'mlb' ? 'run' : sport === 'nhl' ? 'goal' : 'point'} margin`}
                    icon={BoltIcon}
                    iconColor="text-blue-500"
                  />
                  <KPICard
                    title="Total Scoring"
                    value={homeScore + awayScore}
                    subtitle={
                      homeScore + awayScore > 50
                        ? 'High Scoring'
                        : homeScore + awayScore < 30
                        ? 'Low Scoring'
                        : 'Average Scoring'
                    }
                    icon={FireIcon}
                    iconColor="text-orange-500"
                  />
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}

        {/* Player Stats Tab */}
        {boxScore && isFinal && selectedTab === 'players' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 bg-gray-800 border-gray-700 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <UsersIcon className="w-6 h-6 text-purple-500" />
                Player Performance
              </h3>
              {/* NFL-specific: Separate tables by position */}
              {sport === 'nfl' && nflPlayersByPosition ? (
                <div className="space-y-8">
                  {/* Quarterbacks */}
                  {nflPlayersByPosition.qbs.length > 0 && (
                    <div>
                      <h4 className="text-xl font-semibold text-blue-400 mb-4">Quarterbacks</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b border-gray-700">
                              <th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 sticky left-0 z-10">
                                Player
                              </th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Comp/Att</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Pass YDS</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Pass TD</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">INT</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rush YDS</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rush TD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nflPlayersByPosition.qbs.map((player, idx) => {
                              const passYardsTrend = calculateNFLTrend(player.passing_yards || 0, player.season_passing_yards || 0, player.season_games_played);
                              const passTDTrend = calculateNFLTrend(player.passing_touchdowns || 0, player.season_passing_tds || 0, player.season_games_played);
                              const intTrend = calculateNFLTrend(player.interceptions || 0, player.season_interceptions || 0, player.season_games_played);
                              const rushYardsTrend = calculateNFLTrend(player.rushing_yards || 0, player.season_rushing_yards || 0, player.season_games_played);
                              const emoji = getNFLPerformanceEmoji(player);

                              return (
                                <tr key={player.player_id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                                  <td className="px-4 py-4 sticky left-0 bg-gray-800">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold" style={{ color: getTeamColor(player.team_id) || '#D1D5DB' }}>
                                        {player.player_name || `Player #${player.player_id}`}
                                      </span>
                                      {emoji && <span className="text-lg">{emoji}</span>}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-gray-300">
                                    <div className="flex flex-col">
                                      <span className="font-bold">{player.passing_completions || 0}/{player.passing_attempts || 0}</span>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_passing_yards || 0) / player.season_games_played).toFixed(0)} yds/gm</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${passYardsTrend === 'up' ? 'bg-green-900/20' : passYardsTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={`font-bold ${getTrendColorClass(passYardsTrend)}`}>{player.passing_yards || 0}</span>
                                        {passYardsTrend !== 'neutral' && <span className={getTrendColorClass(passYardsTrend)}>{getTrendArrow(passYardsTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_passing_yards || 0) / player.season_games_played).toFixed(0)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${passTDTrend === 'up' ? 'bg-green-900/20' : passTDTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(passTDTrend)}>{player.passing_touchdowns || 0}</span>
                                        {passTDTrend !== 'neutral' && <span className={getTrendColorClass(passTDTrend)}>{getTrendArrow(passTDTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_passing_tds || 0) / player.season_games_played).toFixed(1)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${intTrend === 'down' ? 'bg-green-900/20' : intTrend === 'up' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={intTrend === 'down' ? 'text-green-400' : intTrend === 'up' ? 'text-red-400' : 'text-gray-400'}>{player.interceptions || 0}</span>
                                        {intTrend !== 'neutral' && <span className={intTrend === 'down' ? 'text-green-400' : 'text-red-400'}>{intTrend === 'up' ? '↑' : '↓'}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_interceptions || 0) / player.season_games_played).toFixed(1)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${rushYardsTrend === 'up' ? 'bg-green-900/20' : rushYardsTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(rushYardsTrend)}>{player.rushing_yards || 0}</span>
                                        {rushYardsTrend !== 'neutral' && <span className={getTrendColorClass(rushYardsTrend)}>{getTrendArrow(rushYardsTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_rushing_yards || 0) / player.season_games_played).toFixed(0)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-gray-300">{player.rushing_touchdowns || 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Running Backs */}
                  {nflPlayersByPosition.rbs.length > 0 && (
                    <div>
                      <h4 className="text-xl font-semibold text-green-400 mb-4">Running Backs</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b border-gray-700">
                              <th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 sticky left-0 z-10">Player</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rush Att</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rush YDS</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rush TD</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">REC</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rec YDS</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rec TD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nflPlayersByPosition.rbs.map((player, idx) => {
                              const rushYardsTrend = calculateNFLTrend(player.rushing_yards || 0, player.season_rushing_yards || 0, player.season_games_played);
                              const rushTDTrend = calculateNFLTrend(player.rushing_touchdowns || 0, player.season_rushing_tds || 0, player.season_games_played);
                              const recYardsTrend = calculateNFLTrend(player.receiving_yards || 0, player.season_receiving_yards || 0, player.season_games_played);
                              const emoji = getNFLPerformanceEmoji(player);

                              return (
                                <tr key={player.player_id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                                  <td className="px-4 py-4 sticky left-0 bg-gray-800">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold" style={{ color: getTeamColor(player.team_id) || '#D1D5DB' }}>
                                        {player.player_name || `Player #${player.player_id}`}
                                      </span>
                                      {emoji && <span className="text-lg">{emoji}</span>}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-gray-300">{player.rushing_attempts || 0}</td>
                                  <td className={`px-3 py-4 text-center text-sm ${rushYardsTrend === 'up' ? 'bg-green-900/20' : rushYardsTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={`font-bold ${getTrendColorClass(rushYardsTrend)}`}>{player.rushing_yards || 0}</span>
                                        {rushYardsTrend !== 'neutral' && <span className={getTrendColorClass(rushYardsTrend)}>{getTrendArrow(rushYardsTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_rushing_yards || 0) / player.season_games_played).toFixed(0)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${rushTDTrend === 'up' ? 'bg-green-900/20' : rushTDTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(rushTDTrend)}>{player.rushing_touchdowns || 0}</span>
                                        {rushTDTrend !== 'neutral' && <span className={getTrendColorClass(rushTDTrend)}>{getTrendArrow(rushTDTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_rushing_tds || 0) / player.season_games_played).toFixed(1)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-gray-300">{player.receptions || 0}</td>
                                  <td className={`px-3 py-4 text-center text-sm ${recYardsTrend === 'up' ? 'bg-green-900/20' : recYardsTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(recYardsTrend)}>{player.receiving_yards || 0}</span>
                                        {recYardsTrend !== 'neutral' && <span className={getTrendColorClass(recYardsTrend)}>{getTrendArrow(recYardsTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_receiving_yards || 0) / player.season_games_played).toFixed(0)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-gray-300">{player.receiving_touchdowns || 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Receivers (WR/TE) */}
                  {nflPlayersByPosition.receivers.length > 0 && (
                    <div>
                      <h4 className="text-xl font-semibold text-purple-400 mb-4">Receivers</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className="border-b border-gray-700">
                              <th className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 sticky left-0 z-10">Player</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">REC</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rec YDS</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">Rec TD</th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">YDS/REC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {nflPlayersByPosition.receivers.map((player, idx) => {
                              const recTrend = calculateNFLTrend(player.receptions || 0, player.season_receptions || 0, player.season_games_played);
                              const recYardsTrend = calculateNFLTrend(player.receiving_yards || 0, player.season_receiving_yards || 0, player.season_games_played);
                              const recTDTrend = calculateNFLTrend(player.receiving_touchdowns || 0, player.season_receiving_tds || 0, player.season_games_played);
                              const emoji = getNFLPerformanceEmoji(player);

                              return (
                                <tr key={player.player_id || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                                  <td className="px-4 py-4 sticky left-0 bg-gray-800">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold" style={{ color: getTeamColor(player.team_id) || '#D1D5DB' }}>
                                        {player.player_name || `Player #${player.player_id}`}
                                      </span>
                                      {emoji && <span className="text-lg">{emoji}</span>}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${recTrend === 'up' ? 'bg-green-900/20' : recTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(recTrend)}>{player.receptions || 0}</span>
                                        {recTrend !== 'neutral' && <span className={getTrendColorClass(recTrend)}>{getTrendArrow(recTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_receptions || 0) / player.season_games_played).toFixed(1)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${recYardsTrend === 'up' ? 'bg-green-900/20' : recYardsTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={`font-bold ${getTrendColorClass(recYardsTrend)}`}>{player.receiving_yards || 0}</span>
                                        {recYardsTrend !== 'neutral' && <span className={getTrendColorClass(recYardsTrend)}>{getTrendArrow(recYardsTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_receiving_yards || 0) / player.season_games_played).toFixed(0)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className={`px-3 py-4 text-center text-sm ${recTDTrend === 'up' ? 'bg-green-900/20' : recTDTrend === 'down' ? 'bg-red-900/20' : ''}`}>
                                    <div className="flex flex-col items-center">
                                      <div className="flex items-center gap-1">
                                        <span className={getTrendColorClass(recTDTrend)}>{player.receiving_touchdowns || 0}</span>
                                        {recTDTrend !== 'neutral' && <span className={getTrendColorClass(recTDTrend)}>{getTrendArrow(recTDTrend)}</span>}
                                      </div>
                                      {player.season_games_played > 0 && (
                                        <span className="text-xs text-gray-500">Avg: {((player.season_receiving_tds || 0) / player.season_games_played).toFixed(1)}</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-4 text-center text-sm text-blue-400">
                                    {player.receptions > 0 ? (player.receiving_yards / player.receptions).toFixed(1) : '0.0'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : boxScore.player_stats && boxScore.player_stats.length > 0 ? (
                <>
                  <div className="mb-4 flex items-center gap-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="text-lg">🔥</span> Hot
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-lg">❄️</span> Cold
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-lg">↑</span> Above Avg
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-lg">↓</span> Below Avg
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-lg">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th
                            className="px-4 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 sticky left-0 cursor-pointer hover:bg-gray-800/70 transition-colors"
                            onClick={() => handleSort('player_name')}
                          >
                            <div className="flex items-center gap-1">
                              Player
                              {sortColumn === 'player_name' && (
                                <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                              )}
                            </div>
                          </th>
                          {sport === 'nba' && (
                            <>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('points')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  PTS
                                  {sortColumn === 'points' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('total_rebounds')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  REB
                                  {sortColumn === 'total_rebounds' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('assists')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  AST
                                  {sortColumn === 'assists' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('steals')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  STL
                                  {sortColumn === 'steals' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('blocks')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  BLK
                                  {sortColumn === 'blocks' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                            </>
                          )}
                          {sport === 'nfl' && (
                            <>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('passing_yards')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  YDS
                                  {sortColumn === 'passing_yards' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('passing_touchdowns')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  TD
                                  {sortColumn === 'passing_touchdowns' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('receptions')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  REC
                                  {sortColumn === 'receptions' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">
                                POS
                              </th>
                            </>
                          )}
                          {sport === 'mlb' && (
                            <>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('hits')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  H
                                  {sortColumn === 'hits' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('home_runs')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  HR
                                  {sortColumn === 'home_runs' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('rbi')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  RBI
                                  {sortColumn === 'rbi' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('runs')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  R
                                  {sortColumn === 'runs' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                            </>
                          )}
                          {sport === 'nhl' && (
                            <>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('goals')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  G
                                  {sortColumn === 'goals' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('assists')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  A
                                  {sortColumn === 'assists' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('points')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  PTS
                                  {sortColumn === 'points' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th
                                className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50 cursor-pointer hover:bg-gray-800/70 transition-colors"
                                onClick={() => handleSort('shots')}
                              >
                                <div className="flex items-center justify-center gap-1">
                                  SOG
                                  {sortColumn === 'shots' && (
                                    <span className="text-blue-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                  )}
                                </div>
                              </th>
                              <th className="px-3 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider bg-gray-900/50">
                                TOI
                              </th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPlayerStats.map((player, idx) => (
                          <PlayerStatsRow
                            key={player.player_id || idx}
                            player={player}
                            sport={sport}
                            teamColor={getTeamColor(player.team_id)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <UsersIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No player statistics available for this game</p>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Message for scheduled games */}
        {!isFinal && !isLive && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            <Card className="p-12 text-center bg-gray-800 border-gray-700 shadow-xl">
              <ChartBarIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Game Yet to Start</h3>
              <p className="text-gray-400">
                Detailed statistics and analytics will be available after the game is completed
              </p>
              {game.venue && (
                <p className="text-sm text-gray-500 mt-4">
                  📍 {game.venue} {game.tv_network && `• 📺 ${game.tv_network}`}
                </p>
              )}
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Helper Components

const QuickStat = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 uppercase mb-1">{label}</p>
    <p className="text-lg font-bold text-white">{value}</p>
  </div>
);

const KPICard = ({ title, value, subtitle, icon: Icon, iconColor }) => (
  <div className="bg-gray-900 p-4 rounded-lg">
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-gray-400">{title}</p>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <p className="text-2xl font-bold text-white mb-1">{value}</p>
    <p className="text-xs text-gray-500">{subtitle}</p>
  </div>
);

const DetailedStats = ({ boxScore, sport }) => {
  const homeStats = boxScore.home_team_stats || {};
  const awayStats = boxScore.away_team_stats || {};

  if (sport === 'nfl' || sport === 'ncaa_f') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-semibold text-blue-400 mb-3">Offensive Statistics</h4>
          <StatRow
            label="Passing Yards"
            awayValue={awayStats.passing_yards || 0}
            homeValue={homeStats.passing_yards || 0}
          />
          <StatRow
            label="Rushing Yards"
            awayValue={awayStats.rushing_yards || 0}
            homeValue={homeStats.rushing_yards || 0}
          />
          <StatRow
            label="Total Yards"
            awayValue={(awayStats.passing_yards || 0) + (awayStats.rushing_yards || 0)}
            homeValue={(homeStats.passing_yards || 0) + (homeStats.rushing_yards || 0)}
          />
        </div>
        <div>
          <h4 className="text-lg font-semibold text-yellow-400 mb-3">Game Statistics</h4>
          <StatRow
            label="Turnovers"
            awayValue={awayStats.turnovers || 0}
            homeValue={homeStats.turnovers || 0}
            invertColors
          />
          {homeStats.time_of_possession && (
            <StatRow
              label="Time of Possession"
              awayValue={awayStats.time_of_possession}
              homeValue={homeStats.time_of_possession}
              isNumeric={false}
            />
          )}
        </div>
      </div>
    );
  } else if (sport === 'nhl') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-semibold text-blue-400 mb-3">Hockey Statistics</h4>
          <StatRow label="Shots on Goal" awayValue={awayStats.shots || 0} homeValue={homeStats.shots || 0} />
          <StatRow
            label="Power Play"
            awayValue={`${awayStats.power_play_goals || 0}/${awayStats.power_play_opportunities || 0}`}
            homeValue={`${homeStats.power_play_goals || 0}/${homeStats.power_play_opportunities || 0}`}
            isNumeric={false}
          />
          <StatRow
            label="Penalty Minutes"
            awayValue={awayStats.penalty_minutes || 0}
            homeValue={homeStats.penalty_minutes || 0}
            invertColors
          />
        </div>
      </div>
    );
  } else if (sport === 'nba') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-semibold text-blue-400 mb-3">Shooting Statistics</h4>
          <StatRow
            label="Field Goals"
            awayValue={`${awayStats.fgm || 0}/${awayStats.fga || 0}`}
            homeValue={`${homeStats.fgm || 0}/${homeStats.fga || 0}`}
            isNumeric={false}
          />
          <StatRow
            label="FG%"
            awayValue={`${(awayStats.fg_pct || 0).toFixed(1)}%`}
            homeValue={`${(homeStats.fg_pct || 0).toFixed(1)}%`}
            isNumeric={false}
          />
          <StatRow
            label="3-Pointers"
            awayValue={`${awayStats.three_pm || 0}/${awayStats.three_pa || 0}`}
            homeValue={`${homeStats.three_pm || 0}/${homeStats.three_pa || 0}`}
            isNumeric={false}
          />
          <StatRow
            label="3PT%"
            awayValue={`${(awayStats.three_pct || 0).toFixed(1)}%`}
            homeValue={`${(homeStats.three_pct || 0).toFixed(1)}%`}
            isNumeric={false}
          />
          <StatRow
            label="Free Throws"
            awayValue={`${awayStats.ftm || 0}/${awayStats.fta || 0}`}
            homeValue={`${homeStats.ftm || 0}/${homeStats.fta || 0}`}
            isNumeric={false}
          />
        </div>
        <div>
          <h4 className="text-lg font-semibold text-yellow-400 mb-3">Team Stats</h4>
          <StatRow label="Total Rebounds" awayValue={awayStats.total_rebounds || 0} homeValue={homeStats.total_rebounds || 0} />
          <StatRow label="Assists" awayValue={awayStats.assists || 0} homeValue={homeStats.assists || 0} />
          <StatRow label="Steals" awayValue={awayStats.steals || 0} homeValue={homeStats.steals || 0} />
          <StatRow label="Blocks" awayValue={awayStats.blocks || 0} homeValue={homeStats.blocks || 0} />
          <StatRow label="Turnovers" awayValue={awayStats.turnovers || 0} homeValue={homeStats.turnovers || 0} invertColors />
          <StatRow label="Fouls" awayValue={awayStats.fouls || 0} homeValue={homeStats.fouls || 0} invertColors />
        </div>
      </div>
    );
  } else if (sport === 'mlb') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-lg font-semibold text-blue-400 mb-3">Baseball Statistics</h4>
          <StatRow label="Hits" awayValue={awayStats.hits || 0} homeValue={homeStats.hits || 0} />
          <StatRow
            label="Errors"
            awayValue={awayStats.errors || 0}
            homeValue={homeStats.errors || 0}
            invertColors
          />
          <StatRow
            label="Strikeouts"
            awayValue={awayStats.strikeouts || 0}
            homeValue={homeStats.strikeouts || 0}
          />
        </div>
      </div>
    );
  }
  return null;
};

const StatRow = ({
  label,
  awayValue,
  homeValue,
  awayColor = 'text-red-400',
  homeColor = 'text-blue-400',
  invertColors = false,
  isNumeric = true,
}) => {
  const awayHigher = isNumeric ? awayValue > homeValue : false;
  const homeHigher = isNumeric ? homeValue > awayValue : false;

  const getColor = (isHigher, defaultColor) => {
    if (!isNumeric) return defaultColor;
    if (invertColors) {
      return isHigher ? 'text-gray-500' : defaultColor;
    }
    return isHigher ? 'text-white font-bold' : 'text-gray-500';
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-700 last:border-b-0">
      <div className={`text-right flex-1 ${getColor(awayHigher, awayColor)}`}>
        <span className="text-lg">{awayValue}</span>
      </div>
      <div className="text-center flex-1">
        <span className="text-sm text-gray-400 font-medium">{label}</span>
      </div>
      <div className={`text-left flex-1 ${getColor(homeHigher, homeColor)}`}>
        <span className="text-lg">{homeValue}</span>
      </div>
    </div>
  );
};

export default BoxScore;
